import { useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { visaWorkStatus } from "const/constants";
import { formatDisplayDateTime } from "utils/dateFormat";
import { addVisaStatusEntry, useVisaStatusHistory } from "../visaStatusHistoryApi";

const formatHistoryDate = (value) => formatDisplayDateTime(value);

const getApiError = (err, fallback) =>
  err?.response?.data?.errorMessage || err?.response?.data?.message || fallback;

const VisaStatusHistoryTab = ({ visaId }) => {
  const queryClient = useQueryClient();
  const { data: history = [], isLoading } = useVisaStatusHistory(visaId);
  const [status, setStatus] = useState(null);
  const [note, setNote] = useState("");

  const mutation = useMutation({
    mutationFn: () =>
      addVisaStatusEntry(visaId, { toStatus: status?.label, note: note.trim() || null }),
    onSuccess: () => {
      toast.success("Status recorded");
      setStatus(null);
      setNote("");
      queryClient.invalidateQueries({ queryKey: ["visa-status-history", visaId] });
    },
    onError: (err) => toast.error(getApiError(err, "Failed to record status")),
  });

  const submit = () => {
    if (!status) {
      toast.error("Select a status to add");
      return;
    }
    mutation.mutate();
  };

  return (
    <Stack spacing={3}>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 2 }}>
          Add status update
        </Typography>
        <Box className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Autocomplete
            options={visaWorkStatus}
            value={status}
            onChange={(_, value) => setStatus(value)}
            getOptionLabel={(option) => option?.label || ""}
            renderInput={(params) => <TextField {...params} label="Status" size="small" required />}
          />
          <TextField
            label="Note (optional)"
            size="small"
            className="md:col-span-2"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Additional context for this update"
          />
        </Box>
        <Button
          variant="contained"
          onClick={submit}
          disabled={mutation.isLoading}
          sx={{ mt: 2 }}
          className="ml-auto"
        >
          {mutation.isLoading ? "Submitting…" : "Submit status update"}
        </Button>
      </Paper>

      <Stack spacing={1.5}>
        {isLoading ? (
          <Typography variant="body2" color="text.secondary" className="py-6 text-center">
            Loading status history…
          </Typography>
        ) : history.length === 0 ? (
          <Typography variant="body2" color="text.secondary" className="py-6 text-center">
            No status history yet. Add the first update above.
          </Typography>
        ) : (
          history.map((entry) => (
            <Paper key={entry.id} variant="outlined" sx={{ p: 2 }}>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                <Chip label={entry.toStatus} size="small" color="primary" />
                {entry.fromStatus && (
                  <Typography variant="caption" color="text.secondary">
                    from {entry.fromStatus}
                  </Typography>
                )}
                <Typography variant="caption" color="text.secondary">
                  {formatHistoryDate(entry.createdAt)}
                </Typography>
                {entry.actorName && (
                  <Typography variant="caption" color="text.secondary">
                    — {entry.actorName}
                  </Typography>
                )}
              </Stack>
              {entry.note && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {entry.note}
                </Typography>
              )}
            </Paper>
          ))
        )}
      </Stack>
    </Stack>
  );
};

export default VisaStatusHistoryTab;
